import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { playbackUrl, getSessionToken, type ChannelStream } from "../api";
import { APP_PAUSE_EVENT, APP_RESUME_EVENT } from "../lib/page-lifecycle";
import { publishVideoStats } from "../lib/video-debug";
import { sanitizeLlHlsPlaylistUrl } from "../lib/hls-url";
import { logLiveStep } from "../lib/live-trace";
import { noteChannelPicture } from "../lib/live-watch";
import { measuredHlsDelaySec, startWebrtcPlayback, waitForVideoFrame } from "../lib/webrtc-play";
import { RecordingExportButtons } from "./RecordingExportButtons";
import type { RecordingExportSpec } from "../lib/recording-export";

export type PlayerStatus = "idle" | "connecting" | "live" | "error";

/** Force the media element silent or audible. HLS alternate-audio can ignore a lone `.muted` flag. */
export function applyPlaybackMute(video: HTMLVideoElement | null, mute: boolean, hls?: Hls | null): void {
  if (!video) return;
  video.defaultMuted = mute;
  video.muted = mute;
  video.volume = mute ? 0 : 1;
  const tracks = (video as HTMLVideoElement & { audioTracks?: { length: number; [i: number]: { enabled: boolean } } }).audioTracks;
  if (tracks && tracks.length > 0) {
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track) track.enabled = !mute;
    }
  }
  if (video.srcObject instanceof MediaStream) {
    for (const track of video.srcObject.getAudioTracks()) track.enabled = !mute;
  }
  const media = hls?.media;
  if (media && media !== video) {
    media.muted = mute;
    media.volume = mute ? 0 : 1;
  }
}

function streamHasAudio(video: HTMLVideoElement, hls: Hls | null): boolean {
  if (video.srcObject instanceof MediaStream && video.srcObject.getAudioTracks().length > 0) return true;
  if (hls && hls.audioTracks.length > 0) return true;
  const extended = video as HTMLVideoElement & {
    audioTracks?: { length: number };
    mozHasAudio?: boolean;
    webkitAudioDecodedByteCount?: number;
  };
  if ((extended.audioTracks?.length ?? 0) > 0) return true;
  if (extended.mozHasAudio) return true;
  if ((extended.webkitAudioDecodedByteCount ?? 0) > 0) return true;
  return false;
}

/**
 * H.264 concealment paints missing macroblocks as flat saturated green.
 * A large first segment can still be that frame — size alone is not a picture.
 */
function frameSample(video: HTMLVideoElement): { lit: number; green: number; bottomGreen: number } | null {
  try {
    const w = 48;
    const h = 27;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    let lit = 0;
    let green = 0;
    let bottom = 0;
    let bottomGreen = 0;
    const bottomStart = Math.floor(h * 0.55) * w;
    for (let p = 0, i = 0; i < data.length; i += 4, p += 1) {
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      if (r > 12 || g > 12 || b > 12) lit += 1;
      const conceal = g >= 70 && r <= 48 && b <= 60 && g > r + 35 && g > b + 20;
      if (conceal) green += 1;
      if (p >= bottomStart) {
        bottom += 1;
        if (conceal) bottomGreen += 1;
      }
    }
    return {
      lit,
      green: green / (w * h),
      bottomGreen: bottom > 0 ? bottomGreen / bottom : 0,
    };
  } catch {
    return null;
  }
}

/** A decoded frame with actual picture content. A tiny all-black or green concealment frame stays buffering. */
function decodedPicture(video: HTMLVideoElement, fragBytes = 0): boolean {
  if (!(video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2)) return false;
  const sample = frameSample(video);
  if (sample) {
    if (sample.green > 0.12 || sample.bottomGreen > 0.4) return false;
    if (sample.lit >= 8) return true;
    return false;
  }
  return fragBytes >= 32_000;
}

function roundDelay(sec: number): number {
  return Math.round(sec * 10) / 10;
}

function hlsConstructor(): typeof Hls {
  const mod = Hls as unknown as typeof Hls & { default?: typeof Hls; Hls?: typeof Hls };
  const candidates = [mod, mod.default, mod.Hls].filter(Boolean) as Array<typeof Hls>;
  for (const candidate of candidates) {
    if (typeof candidate === "function" && typeof candidate.isSupported === "function") return candidate;
  }
  for (const candidate of candidates) {
    if (typeof candidate === "function") return candidate;
  }
  return mod;
}

function hlsIsSupported(HlsClass: typeof Hls): boolean {
  try {
    if (typeof HlsClass.isSupported === "function" && HlsClass.isSupported()) return true;
  } catch {
    /* ignore broken export */
  }
  // Prefer attempting hls.js whenever MSE exists — some bundled builds report
  // isSupported()===false even though MediaSource works (verified in-browser).
  return typeof MediaSource !== "undefined" && typeof HlsClass === "function";
}

/**
 * Playback attach uses API-backed readiness (stream.live / hlsHasSegments).
 * Browser fetch-probing index.m3u8 + main_stream.m3u8 caused 404/401 storms
 * while MediaMTX was still warming — do not resurrect it.
 */
function playbackMediaPlaylistUrl(indexOrMediaUrl: string): string {
  if (/main_stream\.m3u8(?:\?|$)/i.test(indexOrMediaUrl)) return indexOrMediaUrl;
  return indexOrMediaUrl.replace(/index\.m3u8(?:\?.*)?$/i, "main_stream.m3u8");
}

export function VideoPlayer({
  deviceLabel,
  deviceName,
  stream,
  deviceOnline,
  traceKey,
  selected,
  delaySec,
  onSelect,
  onRetry,
  onPlay,
  muted: mutedProp,
  embedded = false,
  recoverNonce = 0,
  pageActive = true,
  playingBadge,
  hideDelay = false,
  emptyLabel,
  playback = false,
  playbackUnavailable = false,
  playbackPaused = false,
  playbackRate = 1,
  playbackArmed = true,
  phaseLabel,
  onStatusChange,
  onAudioAvailable,
  onBufferingChange,
  onPlaybackMedia,
  exportClip,
  /** Bumped only on user seek — flushes old HLS buffer; must not restart 0x9201. */
  seekNonce = 0,
  /** After a scrub, stay on the placeholder until the camera frame time matches. */
  seekAttach = false,
  /**
   * Recorded sync: Search-window timeline origin (ms) and master offset from the
   * panel. Not video.currentTime — both tiles share this.
   */
  recordingOriginMs = 0,
  masterOffsetMs = 0,
  /** Absolute playhead ISO for UI clock — never derived from HLS/currentTime. */
  absoluteClockIso,
}: {
  deviceLabel: string;
  deviceName?: string;
  stream: ChannelStream | null;
  deviceOnline: boolean;
  /** deviceId:channel, used to join this tile to the start request. */
  traceKey?: string;
  selected: boolean;
  delaySec: number;
  onSelect: () => void;
  /** Manual restart after a live start failure. Omitted on playback tiles. */
  onRetry?: () => void;
  onPlay?: () => void;
  muted?: boolean;
  embedded?: boolean;
  recoverNonce?: number;
  pageActive?: boolean;
  playingBadge?: string;
  hideDelay?: boolean;
  emptyLabel?: string;
  playback?: boolean;
  playbackUnavailable?: boolean;
  /** Recorded-only: pause the media element (panel also sends 0x9202). */
  playbackPaused?: boolean;
  /** Recorded-only: HTML playbackRate alongside camera 0x9202 speed. */
  playbackRate?: number;
  /** Recorded-only: false until /recordings/start returns — avoids m3u8 404s while 0x9201 is in flight. */
  playbackArmed?: boolean;
  /** Recorded-only pill: SEARCHING, BUFFERING, PLAYING, FAILED, NONE, … */
  phaseLabel?: string;
  onStatusChange?: (status: PlayerStatus) => void;
  onAudioAvailable?: (ok: boolean) => void;
  /** Recorded-only: true while the media element is stalled / waiting for data. */
  onBufferingChange?: (buffering: boolean) => void;
  /** Recorded-only: seconds into the current HLS generation. The panel maps this onto the command start. */
  onPlaybackMedia?: (currentTimeSec: number) => void;
  /** Recorded-only: Download/Share the selected clip. Omitted on live tiles. */
  exportClip?: RecordingExportSpec;
  /** Recorded-only: increment on scrub so old MediaMTX segments are not replayed. */
  seekNonce?: number;
  /** True once post-seek JT1078 time matches the requested recording instant. */
  seekAttach?: boolean;
  recordingOriginMs?: number;
  masterOffsetMs?: number;
  absoluteClockIso?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [playbackAuth, setPlaybackAuth] = useState(false);
  const [internalMuted, setInternalMuted] = useState(!playback);
  const [latency, setLatency] = useState<number | null>(null);
  const [sourceStall, setSourceStall] = useState(false);
  const [codecFault, setCodecFault] = useState(false);
  // Recorded: pin HLS to this tile's channel_no so Rear never probes /pb/ch1.
  // Attach the MediaMTX master playlist (index.m3u8). Direct main_stream.m3u8
  // requests return CDN 401 on MediaMTX v1.21 until the muxer/session is warm;
  // hls.js follows index → main_stream once, without browser probe storms.
  // Live still waits for stream.live. Recorded attaches once playbackArmed.
  const hlsUrl = (() => {
    if (!playback) return stream?.hlsUrl ?? "";
    const ch = Number(stream?.channel_no ?? 0);
    const path = String(stream?.mediamtx_path ?? "").replace(/^\/+|\/+$/g, "");
    if (ch >= 1 && path) {
      const matched = path.match(/^(.*)\/pb\/ch(\d+)(?:\/([a-f0-9]{8}))?$/i);
      const finalPath = matched
        ? `${matched[1]}/pb/ch${ch}${matched[3] ? `/${matched[3].toLowerCase()}` : ""}`
        : path.includes("/pb/")
          ? path
          : `${path}/pb/ch${ch}`;
      // cookieCheck=1 → MediaMTX returns main_stream?session= (no Secure cookie).
      return `/hls/${finalPath}/index.m3u8?cookieCheck=1`;
    }
    if (ch >= 1 && stream?.hlsUrl) {
      const pinnedUrl = stream.hlsUrl
        .replace(/\/pb\/ch\d+\//, `/pb/ch${ch}/`)
        .replace(/main_stream\.m3u8(?:\?.*)?$/i, "index.m3u8");
      return /[?&]cookieCheck=/i.test(pinnedUrl)
        ? pinnedUrl
        : `${pinnedUrl}${pinnedUrl.includes("?") ? "&" : "?"}cookieCheck=1`;
    }
    if (stream?.hlsUrl) {
      const base = stream.hlsUrl.replace(/main_stream\.m3u8(?:\?.*)?$/i, "index.m3u8");
      return /[?&]cookieCheck=/i.test(base) ? base : `${base}${base.includes("?") ? "&" : "?"}cookieCheck=1`;
    }
    return "";
  })();
  const webrtcUrl = playback ? "" : (stream?.webrtcUrl ?? "");
  const streamLive = Boolean(stream?.live);
  const publishing = Boolean(stream?.publishing);
  const liveFailed = !playback && Boolean(stream?.liveFailed) && !streamLive;
  // Once a live playlist has been ready, keep the same hls.js instance across a
  // short "not ready" blip. Destroying it on that blip was the 10s freeze.
  const [latchedLiveUrl, setLatchedLiveUrl] = useState("");
  if (!playback && hlsUrl && streamLive && latchedLiveUrl !== hlsUrl) {
    setLatchedLiveUrl(hlsUrl);
  } else if (!playback && !hlsUrl && latchedLiveUrl) {
    setLatchedLiveUrl("");
  }
  const liveHold = !playback && Boolean(hlsUrl) && latchedLiveUrl === hlsUrl;
  // A tile that already has a picture can ride a short muxer gap. A tile that
  // never decoded must not keep fetching index.m3u8 after the API says the
  // channel is down — those 404s filled the connection pool and the other
  // cameras on the page never started.
  const pictureHeld = liveHold && status === "live";
  // Live: attach when the path is ready or this tile already has a frame.
  // Attaching on a remembered attempt hammers /webrtc with 404s before the upload exists.
  // Recorded: attach when /start has armed the session, even if the first
  // segment is not in the playlist yet. GPS is not part of this gate.
  const attachHold = useRef(false);
  const mediaWanted = !playback && pageActive && (streamLive || publishing || pictureHeld);
  if (mediaWanted) attachHold.current = true;
  if (!playback && !deviceOnline && !streamLive && !publishing) attachHold.current = false;
  const linkUp = deviceOnline || streamLive || publishing || attachHold.current;
  const gate = linkUp && pageActive;
  const playHeld =
    Boolean(stream?.playHeld) && !playback && !streamLive && !publishing && !attachHold.current;
  const canPlay =
    !playHeld &&
    Boolean(hlsUrl || webrtcUrl) &&
    pageActive &&
    !playbackUnavailable &&
    (playback
      // Attach as soon as 0x9201 is armed. The playlist 404s until the first
      // segment exists — that is buffering, not a missing camera.
      ? gate && Boolean(hlsUrl) && playbackArmed
      : (streamLive || publishing || pictureHeld) && (gate || pictureHeld));
  const muted = mutedProp ?? internalMuted;
  const controlled = mutedProp !== undefined;
  const audioCbRef = useRef(onAudioAvailable);
  audioCbRef.current = onAudioAvailable;
  const bufferingCbRef = useRef(onBufferingChange);
  bufferingCbRef.current = onBufferingChange;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const playbackPausedRef = useRef(playbackPaused);
  playbackPausedRef.current = playbackPaused;
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;
  const allowPlayRef = useRef(canPlay);
  allowPlayRef.current = canPlay;
  const pageActiveRef = useRef(pageActive);
  pageActiveRef.current = pageActive;
  const deviceOnlineRef = useRef(deviceOnline);
  deviceOnlineRef.current = deviceOnline;
  const playerAliveRef = useRef(true);
  playerAliveRef.current = true;
  const rtcUrlRef = useRef("");
  const rtcSessionRef = useRef<{ url: string; stop: () => void; framed: boolean } | null>(null);
  const rtcSideRef = useRef<() => void>(() => undefined);
  rtcUrlRef.current = !playback && webrtcUrl ? playbackUrl(webrtcUrl) : "";
  const keepRtc = () => {
    const held = rtcSessionRef.current;
    return Boolean(
      playerAliveRef.current &&
        held &&
        held.url &&
        held.url === rtcUrlRef.current &&
        pageActiveRef.current &&
        deviceOnlineRef.current,
    );
  };
  const releaseRtc = (vehicleId: string, channelNo: number) => {
    rtcSideRef.current();
    const held = rtcSessionRef.current;
    rtcSessionRef.current = null;
    held?.stop();
    if (vehicleId && channelNo > 0) noteChannelPicture(vehicleId, channelNo, false);
  };
  // Do not tie playback attach to recoverNonce — bumping it (start/nudge) was
  // destroying hls.js mid-buffer. Soft recover uses startLoad below instead.
  const attachNonce = 0;
  const seekNonceRef = useRef(seekNonce);
  seekNonceRef.current = seekNonce;
  const seekHoldRef = useRef(false);
  const appliedSeekRef = useRef(0);
  const attachedSeekRef = useRef(0);
  // Absolute Search-window clock is owned by PlaybackPanel. Display it; never
  // invent a time from video.currentTime or HLS duration.
  void recordingOriginMs;
  void masterOffsetMs;
  const absoluteClockLabel = (() => {
    if (!playback || !absoluteClockIso) return null;
    const d = new Date(absoluteClockIso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("en-US", {
      timeZone: "Asia/Kathmandu",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  })();

  const onPlaybackMediaRef = useRef(onPlaybackMedia);
  onPlaybackMediaRef.current = onPlaybackMedia;

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    if (canPlay || rtcSessionRef.current?.framed) return;
    setStatus(liveFailed ? "error" : playbackUnavailable ? "idle" : deviceOnline || streamLive ? "connecting" : "idle");
    setLatency(null);
  }, [canPlay, liveFailed, playbackUnavailable, deviceOnline, streamLive]);

  useEffect(() => {
    if (!playback) return;
    const video = videoRef.current;
    if (!video) return;
    const report = () => {
      if (video.readyState >= 2 && Number.isFinite(video.currentTime)) {
        onPlaybackMediaRef.current?.(video.currentTime);
      }
    };
    video.addEventListener("timeupdate", report);
    const id = window.setInterval(report, 250);
    return () => {
      video.removeEventListener("timeupdate", report);
      window.clearInterval(id);
    };
  }, [playback, hlsUrl]);

  const traceKeyRef = useRef(traceKey);
  traceKeyRef.current = traceKey;
  const channelNoRef = useRef(stream?.channel_no ?? 0);
  channelNoRef.current = stream?.channel_no ?? 0;
  useEffect(() => {
    return () => {
      playerAliveRef.current = false;
      const held = rtcSessionRef.current;
      rtcSessionRef.current = null;
      held?.stop();
      const vehicleId = (traceKeyRef.current ?? "").split(":")[0] ?? "";
      const channelNo = channelNoRef.current;
      if (vehicleId && channelNo > 0) noteChannelPicture(vehicleId, channelNo, false);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const traceVehicle = (traceKey ?? "").split(":")[0] ?? "";
    const traceChannel = stream?.channel_no ?? 0;
    if (!video || !canPlay) {
      if (keepRtc()) {
        return () => {
          if (keepRtc()) return;
          releaseRtc(traceVehicle, traceChannel);
        };
      }
      setStatus(liveFailed ? "error" : playbackUnavailable ? "idle" : deviceOnline ? "connecting" : "idle");
      setLatency(null);
      audioCbRef.current?.(false);
      return;
    }
    const hlsPlayUrl = hlsUrl ? playbackUrl(hlsUrl) : "";
    const rtcPlayUrl = webrtcUrl ? playbackUrl(webrtcUrl) : "";
    if (keepRtc()) {
      return () => {
        if (keepRtc()) return;
        releaseRtc(traceVehicle, traceChannel);
      };
    }
    if (status !== "live") setStatus("connecting");
    publishVideoStats({
      path: stream?.mediamtx_path ?? "",
      channel: stream?.channel_no ?? 0,
      live: Boolean(stream?.live),
      waiting: Boolean(stream?.waiting),
      status: "connecting",
      ws: rtcPlayUrl ? "webrtc" : "hls",
      received: 0,
      decoded: 0,
      rendered: 0,
      dropped: 0,
      firstVideoAt: 0,
    });
    let hls: Hls | null = null;
    let retryTimer: number | undefined;
    let latencyTimer: number | undefined;
    const clearSide = () => {
      window.clearTimeout(retryTimer);
      window.clearInterval(latencyTimer);
    };
    let recovering = false;
    let attempts = 0;
    let playlistAuthTries = 0;
    let destroyed = false;
    let received = 0;
    let decoded = 0;
    let rendered = 0;
    let dropped = 0;
    let firstVideoAt = 0;
    let activeSourceUrl = hlsPlayUrl;
    const healthy = { current: false };
    const seenFrame = { current: false };
    const abort = new AbortController();

    // Prefer a short ahead target while playing; do not delay first-frame "live".
    const PLAYBACK_MIN_BUFFER_SEC = 2;

    const keepMute = () => applyPlaybackMute(video, mutedRef.current, hlsRef.current);
    const reportAudio = () => {
      audioCbRef.current?.(streamHasAudio(video, hlsRef.current));
    };
    const bufferedAheadSec = () => {
      try {
        const t = video.currentTime;
        if (!Number.isFinite(t) || video.buffered.length === 0) return 0;
        let end = 0;
        for (let i = 0; i < video.buffered.length; i++) {
          if (video.buffered.start(i) <= t + 0.15) {
            end = Math.max(end, video.buffered.end(i));
          }
        }
        return Math.max(0, end - t);
      } catch {
        return 0;
      }
    };
    const markLive = (path: "webrtc" | "hls") => {
      healthy.current = true;
      seenFrame.current = true;
      attempts = 0;
      recovering = false;
      if (!firstVideoAt) {
        firstVideoAt = Date.now();
        if (playback) console.info("[PlaybackTiming] first_frame", { at: new Date(firstVideoAt).toISOString() });
        else {
          const vehicleId = (traceKey ?? "").split(":")[0] ?? "";
          const channelNo = stream?.channel_no ?? 0;
          if (rtcSessionRef.current) rtcSessionRef.current.framed = true;
          noteChannelPicture(vehicleId, channelNo, true);
          logLiveStep({ vehicleId, channel: channelNo, step: "FIRST_FRAME" });
          logLiveStep({ vehicleId, channel: channelNo, step: "PLAYING", durationMs: Date.now() - firstVideoAt });
        }
      }
      if (!playback) {
        setSourceStall(false);
        setPlaybackAuth(false);
        playlistAuthTries = 0;
      }
      rendered += 1;
      bufferingCbRef.current?.(false);
      setStatus("live");
      publishVideoStats({
        status: "live",
        ws: path,
        rendered,
        firstVideoAt,
        live: true,
        waiting: false,
      });
    };

    const startHls = (sourceUrl = activeSourceUrl || hlsPlayUrl) => {
      if (destroyed || !allowPlayRef.current) return;
      if (!sourceUrl) {
        if (!destroyed) setStatus("error");
        return;
      }
      activeSourceUrl = sourceUrl;
      publishVideoStats({ ws: "hls" });

      const HlsClass = hlsConstructor();
      if (!hlsIsSupported(HlsClass)) {
        // Last resort: native HLS (Safari / some Chromium builds). MediaMTX master
        // playlists often fail here — prefer hls.js whenever MSE exists.
        if (typeof MediaSource === "undefined" && video.canPlayType("application/vnd.apple.mpegurl")) {
          video.srcObject = null;
          video.src = sourceUrl;
          const markNative = () => {
            if (!playback && !(video.readyState >= 2 && video.videoWidth > 0 && video.currentTime > 0.05)) return;
            markLive("hls");
          };
          void video.play().then(() => markNative()).catch(() => {
            if (!destroyed) setStatus("error");
          });
          const onPlaying = () => markNative();
          video.addEventListener("playing", onPlaying);
          abort.signal.addEventListener("abort", () => video.removeEventListener("playing", onPlaying), { once: true });
          if (!playback) {
            const onTick = () => markNative();
            video.addEventListener("timeupdate", onTick);
            abort.signal.addEventListener("abort", () => video.removeEventListener("timeupdate", onTick), { once: true });
          }
          return;
        }
        if (!destroyed) setStatus("error");
        publishVideoStats({ status: "error", ws: "hls" });
        return;
      }

      video.srcObject = null;
      try {
        // Keep config minimal first — a broken custom loader previously aborted
        // attach before any playlist fetch (Network showed zero /hls requests).
        hls = new HlsClass({
          // Worker keeps MSE decode off the UI thread — fewer freezes on long clips.
          enableWorker: true,
          lowLatencyMode: false,
          xhrSetup(xhr) {
            // /hls/ is gated by the developer session. Send the cookie and the
            // same login token so nginx auth_request accepts the playlist.
            xhr.withCredentials = true;
            const token = getSessionToken();
            if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          },
          ...(playback
            ? {
                // Recorded /pb grows ahead of playout (ffmpeg ingest ASAP with
                // correct FPS). Start at clip begin; stay behind the live edge
                // so a 15–25s SD file-boundary pause is absorbed by the cushion.
                // Never speed up to catch up — that caused visible fast/slow jumps.
                startPosition: 0,
                startFragPrefetch: true,
                liveDurationInfinity: true,
                liveSyncDurationCount: 20,
                liveMaxLatencyDurationCount: 100000,
                maxLiveSyncPlaybackRate: 1,
                // ~2 min forward target, 5 min ceiling. backBufferLength drops
                // old media so a multi-hour recording does not stay in memory.
                maxBufferLength: 120,
                maxMaxBufferLength: 300,
                backBufferLength: 90,
                maxBufferHole: 1.5,
                nudgeOffset: 0.05,
                nudgeMaxRetry: 10,
                manifestLoadingMaxRetry: 8,
                levelLoadingMaxRetry: 8,
                fragLoadingMaxRetry: 12,
              }
            : {
                // MPEG-TS live. lowLatencyMode holds the playlist request open
                // (_HLS_msn) and a wall of those fills the browser socket pool,
                // so /start, talk, and listen never get a connection.
                liveSyncDurationCount: 2,
                startFragPrefetch: true,
                liveMaxLatencyDurationCount: 6,
                liveDurationInfinity: true,
                maxLiveSyncPlaybackRate: 1.15,
                maxBufferLength: 4,
                maxMaxBufferLength: 6,
                backBufferLength: 4,
                maxBufferHole: 2,
                maxSeekHole: 3,
                nudgeOffset: 0.1,
                nudgeMaxRetry: 8,
                maxStarvationDelay: 4,
                // A 401 on main_stream?session= must not retry dozens of times.
                // Eight wall tiles doing that fill the browser connection pool
                // and the other cameras never get a start or a playlist.
                manifestLoadingMaxRetry: 1,
                levelLoadingMaxRetry: 1,
                fragLoadingMaxRetry: 2,
              }),
          manifestLoadingRetryDelay: playback ? 600 : embedded ? 400 : 2000,
          levelLoadingRetryDelay: playback ? 600 : embedded ? 400 : 2000,
          fragLoadingRetryDelay: playback ? 200 : 400,
          // SD file boundaries pause the upload for 15–25s. A 15s load timeout
          // treated that pause as a dead stream and stopped a recording that
          // was still coming from the camera.
          manifestLoadingTimeOut: playback ? 45000 : 5000,
          levelLoadingTimeOut: playback ? 45000 : 5000,
          fragLoadingTimeOut: playback ? 45000 : 5000,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        publishVideoStats({ status: "error", ws: "hls" });
        try {
          (window as unknown as { __dashcamHlsError?: string }).__dashcamHlsError = message;
        } catch {
          /* ignore */
        }
        if (!destroyed) setStatus("error");
        return;
      }
      if (!hls) return;
      hlsRef.current = hls;
      const cleanSource = sanitizeLlHlsPlaylistUrl(sourceUrl);
      activeSourceUrl = cleanSource;
      logLiveStep({
        vehicleId: (traceKey ?? "").split(":")[0] ?? "",
        channel: stream?.channel_no ?? 0,
        step: "hls_session_creation",
      });
      hls.loadSource(cleanSource);
      if (playback) {
        console.info("[PLAYBACK] hls", {
          at: new Date().toISOString(),
          url: cleanSource,
        });
      }
      hls.attachMedia(video);
      publishVideoStats({ status: "connecting", ws: "hls", path: stream?.mediamtx_path ?? "", channel: stream?.channel_no ?? 0 });
      // Ensure the first playlist fetch starts immediately (some builds wait for
      // MEDIA_ATTACHED only, which can stall if the element was previously used).
      try {
        hls.startLoad(playback ? 0 : undefined);
      } catch {
        /* attachMedia will start load */
      }
      // Pin to clip begin once. Never re-pin after a seek (that replayed old
      // MediaMTX segments and looked like duplicated / jumping video).
      let pinnedStart = false;
      const pinRecordedStart = () => {
        if (!playback || pinnedStart || seekNonceRef.current > 0) return;
        try {
          const start = video.seekable.length > 0 ? video.seekable.start(0) : 0;
          if (Number.isFinite(start) && Math.abs(video.currentTime - start) > 0.4) {
            video.currentTime = start;
          }
          pinnedStart = true;
        } catch {
          /* seekable not ready */
        }
      };
      let lastFragBytes = 0;
      let cleanStreak = 0;
      const markDecodedFrame = (path: "webrtc" | "hls") => {
        if (playback && seekHoldRef.current) return;
        if (
          playback &&
          typeof video.requestVideoFrameCallback === "function" &&
          cleanStreak < 3
        ) {
          return;
        }
        // Wait for a real decoded picture — first FRAG alone is often mid-GOP green,
        // and a ~14 KB segment can decode to a sized black frame.
        if (playback && !decodedPicture(video, lastFragBytes)) return;
        // Go live on the first real frame so Search→picture stays within ~10s.
        // Keep buffering deeply while playing; do not hold the UI for a long cushion.
        if (playback) {
          const ahead = bufferedAheadSec();
          if (!seenFrame.current) {
            console.info("[PLAYBACK] first_decoded_frame", {
              at: new Date().toISOString(),
              channel: stream?.channel_no ?? 0,
              buffer_seconds: ahead,
              currentTime: video.currentTime,
            });
          }
          if (ahead < PLAYBACK_MIN_BUFFER_SEC) {
            bufferingCbRef.current?.(true);
            if (!playbackPausedRef.current && video.paused) {
              void video.play().catch(() => undefined);
            }
          } else {
            bufferingCbRef.current?.(false);
          }
        } else if (!(video.readyState >= 2 && video.videoWidth > 0 && video.currentTime > 0.05)) {
          // LIVE only after a decoded frame is on screen and the playhead is moving.
          return;
        }
        markLive(path);
      };
      const videoWithFrame = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (cb: () => void) => number;
        cancelVideoFrameCallback?: (handle: number) => void;
      };
      let frameWatch = 0;
      let startupSample = 0;
      const notePresentedFrame = () => {
        if (destroyed) return;
        if (!playbackPausedRef.current && video.paused && video.readyState >= 2) {
          void video.play().catch(() => undefined);
        }
        const sample = frameSample(video);
        const concealed = Boolean(sample && (sample.green > 0.12 || sample.bottomGreen > 0.4));
        if (concealed) {
          cleanStreak = 0;
          if (seenFrame.current) {
            seenFrame.current = false;
            healthy.current = false;
            setStatus("connecting");
          }
          return;
        }
        if (!sample || sample.lit < 8) return;
        if (cleanStreak < 8) cleanStreak += 1;
        if (cleanStreak >= 3) markDecodedFrame("hls");
      };
      const watchPresentedFrame = () => {
        notePresentedFrame();
        if (typeof videoWithFrame.requestVideoFrameCallback === "function" && cleanStreak < 8) {
          frameWatch = videoWithFrame.requestVideoFrameCallback(watchPresentedFrame);
        }
      };
      if (playback && typeof videoWithFrame.requestVideoFrameCallback === "function") {
        frameWatch = videoWithFrame.requestVideoFrameCallback(watchPresentedFrame);
        startupSample = window.setInterval(() => {
          if (destroyed || cleanStreak >= 8) {
            window.clearInterval(startupSample);
            return;
          }
          notePresentedFrame();
        }, 100);
      }
      hls.on(HlsClass.Events.MEDIA_ATTACHED, keepMute);
      hls.on(HlsClass.Events.FRAG_LOADED, (_evt, data) => {
        received += 1;
        const total = Number(data?.frag?.stats?.total ?? 0);
        if (total > lastFragBytes) lastFragBytes = total;
        publishVideoStats({ received });
        pinRecordedStart();
      });
      hls.on(HlsClass.Events.FRAG_CHANGED, () => {
        decoded += 1;
        if (!firstVideoAt) {
          firstVideoAt = Date.now();
          pinRecordedStart();
        }
        publishVideoStats({ decoded, firstVideoAt });
        markDecodedFrame("hls");
      });
      hls.on(HlsClass.Events.MANIFEST_PARSED, () => {
        recovering = false;
        keepMute();
        reportAudio();
        pinRecordedStart();
        void video.play().then(() => {
          keepMute();
          if (playbackPausedRef.current) {
            try {
              video.pause();
            } catch {
              /* ignore */
            }
          }
          markDecodedFrame("hls");
        }).catch(() => {
          markDecodedFrame("hls");
          if (!healthy.current) setStatus("connecting");
        });
        markDecodedFrame("hls");
        if (!healthy.current) {
          const onMeta = () => {
            video.removeEventListener("loadeddata", onMeta);
            markDecodedFrame("hls");
          };
          video.addEventListener("loadeddata", onMeta);
        }
      });
      hls.on(HlsClass.Events.AUDIO_TRACKS_UPDATED, () => {
        keepMute();
        reportAudio();
      });
      hls.on(HlsClass.Events.AUDIO_TRACK_SWITCHED, keepMute);
      const onPlaying = () => {
        keepMute();
        bufferingCbRef.current?.(false);
        clearStall();
        markDecodedFrame("hls");
      };
      let stallTimer = 0;
      const clearStall = () => {
        window.clearTimeout(stallTimer);
        stallTimer = 0;
        if (!playback) setSourceStall(false);
      };
      const onWaiting = () => {
        if (playback && seenFrame.current) bufferingCbRef.current?.(true);
        if (playback || !seenFrame.current) return;
        // The cushion should keep playing through a late segment. Nudge the
        // existing loader first. The unavailable overlay is only for a stall
        // that does not recover on its own.
        window.clearTimeout(stallTimer);
        stallTimer = window.setTimeout(() => {
          if (destroyed || !seenFrame.current) return;
          if (bufferedAheadSec() > 0.3 && video.readyState >= 2) {
            setSourceStall(false);
            return;
          }
          // Buffer ran dry. Continue this hls.js instance. loadSource() here
          // discarded the frame and the next main_stream request returned 401.
          try {
            hls?.startLoad();
          } catch {
            /* ignore */
          }
          if (video.videoWidth > 0) {
            setSourceStall(false);
            return;
          }
          void video.play().catch(() => undefined);
          stallTimer = window.setTimeout(() => {
            if (destroyed || !seenFrame.current) return;
            if (video.videoWidth > 0 || (bufferedAheadSec() > 0.3 && video.readyState >= 2)) {
              setSourceStall(false);
              return;
            }
            setSourceStall(true);
          }, 8000);
        }, 1500);
      };
      const onCanPlay = () => {
        bufferingCbRef.current?.(false);
        clearStall();
      };
      video.addEventListener("playing", onPlaying);
      video.addEventListener("waiting", onWaiting);
      video.addEventListener("canplay", onCanPlay);
      const onLiveTick = () => {
        if (!playback) markDecodedFrame("hls");
      };
      if (!playback) video.addEventListener("timeupdate", onLiveTick);
      const remountHls = () => {
        if (destroyed || !allowPlayRef.current || !hlsPlayUrl) return;
        window.clearTimeout(retryTimer);
        window.clearInterval(latencyTimer);
        const extra = (startHls as { extraCleanup?: () => void }).extraCleanup;
        extra?.();
        (startHls as { extraCleanup?: () => void }).extraCleanup = undefined;
        try {
          hls?.destroy();
        } catch {
          /* ignore */
        }
        hls = null;
        hlsRef.current = null;
        recovering = false;
        window.setTimeout(() => {
          if (!destroyed && allowPlayRef.current) startHls();
        }, 200);
      };
      const scheduleRetry = () => {
        if (destroyed) return;
        if (!allowPlayRef.current) {
          hls?.stopLoad();
          if (video.readyState < 2 && !seenFrame.current) setStatus("connecting");
          return;
        }
        // After the first frame, keep REC/live chrome — never flash CONNECTING on
        // brief segment 404s (MediaMTX rolling playlists drop old .ts files).
        if (!seenFrame.current && (video.readyState < 2 || !healthy.current)) {
          setStatus("connecting");
        }
        const delay = Math.min(400 * 2 ** Math.min(attempts, 3), 2500);
        attempts += 1;
        retryTimer = window.setTimeout(() => {
          if (destroyed || !allowPlayRef.current) return;
          recovering = false;
          try {
            if (playback && attempts > 6 && !seenFrame.current) {
              remountHls();
              return;
            }
            // Already playing recorded: soft-continue only — never remount hls.js
            // when new camera packets arrive (that caused 401 + grey flicker).
            if (playback && seenFrame.current) {
              try {
                hls?.startLoad();
              } catch {
                /* ignore */
              }
              return;
            }
            if (!hls) {
              startHls();
              return;
            }
            if (playback) {
              // Do not startLoad(0) — that re-fetched old MediaMTX segments and
              // caused duplicate/backward jumps. Soft continue from current.
              hls.startLoad();
            } else if (seenFrame.current && bufferedAheadSec() > 0.3) {
              // Healthy live picture: leave the loader alone.
            } else {
              // Continue the same hls.js instance. loadSource() here discarded
              // the live buffer and restarted the playhead.
              hls.startLoad();
            }
          } catch {
            hls?.startLoad();
          }
        }, delay);
      };
      let loggedPlaylist = false;
      let loggedSegment = false;
      hls.on(HlsClass.Events.MANIFEST_LOADED, () => {
        if (loggedPlaylist || playback) return;
        loggedPlaylist = true;
        logLiveStep({
          vehicleId: (traceKey ?? "").split(":")[0] ?? "",
          channel: stream?.channel_no ?? 0,
          step: "hls_playlist_authorization",
          httpStatus: 200,
        });
      });
      hls.on(HlsClass.Events.FRAG_LOADED, () => {
        if (loggedSegment || playback) return;
        loggedSegment = true;
        logLiveStep({
          vehicleId: (traceKey ?? "").split(":")[0] ?? "",
          channel: stream?.channel_no ?? 0,
          step: "hls_segment_authorization",
          httpStatus: 200,
        });
      });
      hls.on(HlsClass.Events.ERROR, (_e, data) => {
        if (!playback && data.fatal && data.type === HlsClass.ErrorTypes.MEDIA_ERROR) {
          setCodecFault(true);
          logLiveStep({
            vehicleId: (traceKey ?? "").split(":")[0] ?? "",
            channel: stream?.channel_no ?? 0,
            step: "player_codec",
            error: data.details || "codec_error",
          });
        }
        const code = data.response?.code;
        // MediaMTX 401/403 on main_stream?session= is that HLS session, not the
        // dashboard login. nginx already accepted the session cookie. Retrying
        // startLoad() hits the same dead session and floods the connection pool.
        if (!playback && (code === 401 || code === 403)) {
          const failedUrl = data.url || data.frag?.url || "";
          const sessionId = /[?&]session=([^&]+)/.exec(failedUrl)?.[1] ?? "";
          const segment = /\.ts(?:\?|$)/i.test(failedUrl);
          logLiveStep({
            vehicleId: (traceKey ?? "").split(":")[0] ?? "",
            channel: stream?.channel_no ?? 0,
            step: segment ? "hls_segment_authorization" : "hls_playlist_authorization",
            error: code === 403 ? "forbidden" : "hls_session_rejected",
            httpStatus: code,
            sessionId,
          });
          if (seenFrame.current && video.videoWidth > 0 && bufferedAheadSec() > 0.3) return;
          if (recovering) return;
          recovering = true;
          try {
            hls?.stopLoad();
          } catch {
            /* ignore */
          }
          playlistAuthTries += 1;
          if (playlistAuthTries > 4) {
            recovering = false;
            setPlaybackAuth(true);
            return;
          }
          setPlaybackAuth(false);
          const delay = Math.min(500 * 2 ** (playlistAuthTries - 1), 8_000);
          window.clearTimeout(retryTimer);
          retryTimer = window.setTimeout(() => {
            recovering = false;
            if (destroyed || !allowPlayRef.current || !hls) return;
            const base = hlsPlayUrl || activeSourceUrl;
            if (!base) return;
            const fresh = `${base}${base.includes("?") ? "&" : "?"}r=${Date.now()}`;
            try {
              hls.loadSource(fresh);
              hls.startLoad();
            } catch {
              /* ignore */
            }
          }, delay);
          return;
        }
        // 404/5xx while the muxer is opening is warmup. A 401 is handled above.
        if (code === 401 || code === 404 || code === 500 || code === 502 || code === 503 || code === 504) {
          // Reloading the playlist here threw away the live buffer and looked
          // like a multi-second freeze. Keep the bytes already in the player.
          if (!playback && seenFrame.current) {
            // The picture is already on screen. Another playlist reload is what
            // turned a playing tile into "temporarily unavailable" (401).
            // A dry buffer still needs this same hls.js instance to keep loading.
            if (video.videoWidth > 0 && bufferedAheadSec() > 0.3) {
              setSourceStall(false);
              return;
            }
            try {
              hls?.startLoad();
            } catch {
              /* ignore */
            }
            return;
          }
          // Already decoding: ignore transient playlist errors — scheduleRetry →
          // startLoad(0) was flooding Network with hundreds of main_stream requests.
          if (playback && seenFrame.current) {
            // The JT1078 session is still the source of truth. A playlist gap
            // at a file boundary must not destroy hls.js or freeze a player
            // that still has the backend upload.
            bufferingCbRef.current?.(bufferedAheadSec() < 0.4);
            try {
              hls?.startLoad();
            } catch {
              /* ignore */
            }
            return;
          }
          // Live warmup 404s are the muxer opening. Keep this hls.js instance and
          // retry. stopLoad() here left the tile on "Waiting" until a remount.
          // Not decoding yet. 401/404/500 while the first segment is opening is
          // buffering — the JT1078 session is still producing media. Keep this
          // hls.js instance and retry. Do not stopLoad or mark the camera NONE.
          if (playback && !seenFrame.current) {
            if (video.readyState < 2) setStatus("connecting");
            bufferingCbRef.current?.(true);
            attempts += 1;
            if (attempts > 12) attempts = 8;
            const delay = Math.min(800 * 2 ** Math.min(attempts, 3), 4_000);
            retryTimer = window.setTimeout(() => {
              if (destroyed || !allowPlayRef.current) return;
              try {
                // Continue load — do not force position 0 (401 storm + green join).
                hls?.startLoad();
              } catch {
                /* ignore */
              }
            }, delay);
            return;
          }
          if (!playback) {
            if (!seenFrame.current && video.readyState < 2) setStatus("connecting");
            // hls.js already used its short retry budget. Reloading the same
            // main_stream?session= URL only repeats 401. Ask for a fresh session.
            if (data.fatal && !recovering && allowPlayRef.current) {
              recovering = true;
              window.clearTimeout(retryTimer);
              retryTimer = window.setTimeout(() => {
                recovering = false;
                if (destroyed || !allowPlayRef.current) return;
                try {
                  if (hls && hlsPlayUrl) hls.loadSource(hlsPlayUrl);
                  else hls?.startLoad();
                } catch {
                  /* ignore */
                }
              }, 2500);
            }
            return;
          }
          if (playback && (code === 401 || code === 500 || code === 404) && attempts >= 24) {
            attempts = 8;
          }
          if (!seenFrame.current) {
            healthy.current = false;
            if (video.readyState < 2) setStatus("connecting");
          }
          if (allowPlayRef.current) scheduleRetry();
          return;
        }
        if (!data.fatal) return;
        if (video.readyState >= 2 && (healthy.current || seenFrame.current) && data.type === HlsClass.ErrorTypes.NETWORK_ERROR) {
          recovering = true;
          hls?.startLoad();
          return;
        }
        if (seenFrame.current && playback && data.type === HlsClass.ErrorTypes.MEDIA_ERROR) {
          // Soft recover only — full remount mid-clip flashed solid grey and cancelled .ts.
          if (!recovering) {
            recovering = true;
            bufferingCbRef.current?.(true);
            try {
              hls?.recoverMediaError();
            } catch {
              try {
                hls?.startLoad();
              } catch {
                /* ignore */
              }
            }
            window.setTimeout(() => {
              recovering = false;
            }, 1500);
          }
          return;
        }
        if (seenFrame.current && playback) {
          try {
            hls?.startLoad();
          } catch {
            /* ignore */
          }
          return;
        }
        if (!playback && seenFrame.current) {
          if (!recovering) {
            recovering = true;
            try {
              if (data.type === HlsClass.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
              else hls?.startLoad();
            } catch {
              /* ignore */
            }
            window.setTimeout(() => {
              recovering = false;
            }, 1500);
          }
          return;
        }
        healthy.current = false;
        if (!recovering && data.type === HlsClass.ErrorTypes.NETWORK_ERROR) {
          recovering = true;
          if (video.readyState < 2) setStatus("connecting");
          hls?.startLoad();
          return;
        }
        if (!recovering && data.type === HlsClass.ErrorTypes.MEDIA_ERROR) {
          recovering = true;
          hls?.recoverMediaError();
          return;
        }
        scheduleRetry();
      });
      const onOnline = () => {
        if (!hls || !allowPlayRef.current || !pageActiveRef.current) return;
        hls.startLoad();
      };
      const onAppPause = () => {
        video.pause();
        hls?.stopLoad();
      };
      const resumeIfNeeded = () => {
        if (!hls || document.visibilityState === "hidden" || !pageActiveRef.current) return;
        if (!allowPlayRef.current) {
          hls.stopLoad();
          return;
        }
        if (healthy.current && video.readyState >= 2) {
          void video.play().catch(() => undefined);
          return;
        }
        hls.startLoad();
      };
      window.addEventListener("online", onOnline);
      document.addEventListener("visibilitychange", resumeIfNeeded);
      window.addEventListener("pageshow", resumeIfNeeded);
      window.addEventListener(APP_RESUME_EVENT, resumeIfNeeded);
      window.addEventListener(APP_PAUSE_EVENT, onAppPause);
      latencyTimer = window.setInterval(() => {
        if (!hls || !video) return;
        const lat = measuredHlsDelaySec(hls);
        if (lat != null) {
          setLatency(roundDelay(lat));
          // Stay on the buffered timeline. Seeking to liveSyncPosition discarded
          // the ~10s cushion and made live video jump every few seconds.
          publishVideoStats({ latencySec: lat });
        }
        keepMute();
        reportAudio();
      }, 250);
      const existingCleanup = () => {
        window.clearInterval(startupSample);
        if (frameWatch && typeof videoWithFrame.cancelVideoFrameCallback === "function") {
          videoWithFrame.cancelVideoFrameCallback(frameWatch);
        }
        window.removeEventListener("online", onOnline);
        document.removeEventListener("visibilitychange", resumeIfNeeded);
        window.removeEventListener("pageshow", resumeIfNeeded);
        window.removeEventListener(APP_RESUME_EVENT, resumeIfNeeded);
        window.removeEventListener(APP_PAUSE_EVENT, onAppPause);
        window.clearTimeout(stallTimer);
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("waiting", onWaiting);
        video.removeEventListener("canplay", onCanPlay);
        video.removeEventListener("timeupdate", onLiveTick);
        bufferingCbRef.current?.(false);
      };
      (startHls as { extraCleanup?: () => void }).extraCleanup = existingCleanup;
    };

    const stopWebrtc = () => {
      if (video.srcObject) video.srcObject = null;
    };

    // Recorded: canPlay already requires stream.live (API EXTINF probe). Attach
    // main_stream once — never browser-poll m3u8 (that was the 404/401 storm).
    let webrtcPlay: { stop: () => void } | null = null;
    const vehicleId = (traceKey ?? "").split(":")[0] ?? "";
    const channelNo = stream?.channel_no ?? 0;

    const beginLive = async () => {
      if (destroyed || !allowPlayRef.current) return;
      if (rtcPlayUrl) {
        const mine = { url: rtcPlayUrl, stop: () => abort.abort(), framed: false };
        rtcSessionRef.current = mine;
        const rtcBegan = Date.now();
        const rtc = await startWebrtcPlayback({
          url: rtcPlayUrl,
          video,
          signal: abort.signal,
          connectTimeoutMs: 4_000,
          trace: { vehicleId, channel: channelNo },
        });
        if (destroyed || abort.signal.aborted) {
          rtc?.stop();
          if (rtcSessionRef.current === mine) rtcSessionRef.current = null;
          return;
        }
        if (!rtc) {
          if (rtcSessionRef.current === mine) rtcSessionRef.current = null;
        } else {
          mine.stop = () => {
            abort.abort();
            rtc.stop();
          };
          logLiveStep({
            vehicleId,
            channel: channelNo,
            step: "WEBRTC_READY",
            durationMs: Date.now() - rtcBegan,
          });
          const iceFailed = new Promise<boolean>((resolve) => {
            let settled = false;
            const finish = (ok: boolean) => {
              if (settled) return;
              settled = true;
              rtc.pc.removeEventListener("connectionstatechange", onState);
              rtc.pc.removeEventListener("iceconnectionstatechange", onState);
              resolve(ok);
            };
            const onState = () => {
              if (rtc.pc.connectionState === "failed" || rtc.pc.iceConnectionState === "failed") finish(false);
            };
            if (rtc.pc.connectionState === "failed" || rtc.pc.iceConnectionState === "failed") {
              finish(false);
              return;
            }
            rtc.pc.addEventListener("connectionstatechange", onState);
            rtc.pc.addEventListener("iceconnectionstatechange", onState);
            abort.signal.addEventListener("abort", () => finish(false), { once: true });
          });
          const frame = await Promise.race([
            waitForVideoFrame(video, abort.signal, 6_000),
            iceFailed,
          ]);
          if (destroyed || abort.signal.aborted) {
            rtc.stop();
            return;
          }
          if (frame) {
            webrtcPlay = rtc;
            setCodecFault(false);
            markLive("webrtc");
            logLiveStep({
              vehicleId,
              channel: channelNo,
              step: "player_webrtc",
              durationMs: Date.now() - rtcBegan,
            });
            latencyTimer = window.setInterval(() => {
              void rtc.latencySec().then((lat) => {
                if (lat != null && !destroyed) setLatency(roundDelay(lat));
              });
            }, 1000);
            return;
          }
          rtc.stop();
          if (rtcSessionRef.current === mine) rtcSessionRef.current = null;
          if (video.srcObject) video.srcObject = null;
          logLiveStep({
            vehicleId,
            channel: channelNo,
            step: "player_webrtc",
            error: "no_frame",
            durationMs: Date.now() - rtcBegan,
          });
        }
      }
      if (!destroyed && allowPlayRef.current && hlsPlayUrl) {
        logLiveStep({ vehicleId, channel: channelNo, step: "HLS_READY" });
        logLiveStep({ vehicleId, channel: channelNo, step: "player_hls" });
        startHls(hlsPlayUrl);
      }
    };

    rtcSideRef.current = clearSide;

    if (playback) {
      if (allowPlayRef.current && hlsPlayUrl) startHls(hlsPlayUrl);
    } else if (allowPlayRef.current && (rtcPlayUrl || hlsPlayUrl)) {
      void beginLive();
    }

    return () => {
      if (keepRtc()) return;
      destroyed = true;
      releaseRtc(traceVehicle, traceChannel);
      abort.abort();
      window.clearTimeout(retryTimer);
      window.clearInterval(latencyTimer);
      logLiveStep({ vehicleId: traceVehicle, channel: traceChannel, step: "disconnect" });
      webrtcPlay?.stop();
      stopWebrtc();
      const extra = (startHls as { extraCleanup?: () => void }).extraCleanup;
      extra?.();
      hls?.destroy();
      hlsRef.current = null;
      video.removeAttribute("src");
      video.load();
    };
  }, [hlsUrl, webrtcUrl, canPlay, playback, playbackUnavailable, attachNonce]);

  useEffect(() => {
    playerAliveRef.current = true;
    return () => {
      playerAliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!recoverNonce || !pageActive) return;
    const video = videoRef.current;
    if (!video || !canPlay) return;
    const hls = hlsRef.current;
    if (playback && hls) {
      // Already decoding: never startLoad(0) — that re-pulled old /pb segments,
      // caused 401 storms, green joins, and duplicated video. Only ensure play().
      if (status === "live" || video.readyState >= 2) {
        if (!playbackPausedRef.current) void video.play().catch(() => undefined);
        return;
      }
      try {
        hls.startLoad();
      } catch {
        /* ignore */
      }
      if (playbackPausedRef.current) {
        try {
          video.pause();
        } catch {
          /* ignore */
        }
      } else {
        void video.play().catch(() => undefined);
      }
      return;
    }
    if (video.readyState >= 2 && !video.paused) return;
    if (!playback && hls && hlsUrl) {
      const fresh = `${hlsUrl}${hlsUrl.includes("?") ? "&" : "?"}r=${Date.now()}`;
      try {
        hls.loadSource(fresh);
        hls.startLoad();
      } catch {
        hls.startLoad();
      }
    } else {
      hls?.startLoad();
    }
    void video.play().catch(() => undefined);
  }, [recoverNonce, pageActive, canPlay, playback]);

  // User scrub: hide the pre-seek picture immediately. Do not loadSource until
  // seekAttach — that reload was replaying the start of the old playlist
  // (burned-in clock rewound) while the timeline showed the scrub target.
  useEffect(() => {
    if (!playback || !seekNonce) return;
    if (appliedSeekRef.current === seekNonce) return;
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls || !canPlay) return;
    appliedSeekRef.current = seekNonce;
    seekHoldRef.current = true;
    bufferingCbRef.current?.(true);
    setStatus("connecting");
    try {
      hls.stopLoad();
    } catch {
      /* ignore */
    }
    try {
      video.pause();
    } catch {
      /* ignore */
    }
  }, [seekNonce, playback, canPlay]);

  useEffect(() => {
    if (!playback || !seekNonce || !seekAttach) return;
    if (attachedSeekRef.current === seekNonce) return;
    const video = videoRef.current;
    const hls = hlsRef.current;
    if (!video || !hls || !canPlay) return;
    attachedSeekRef.current = seekNonce;
    seekHoldRef.current = false;
    bufferingCbRef.current?.(true);
    setStatus("connecting");
    const source = sanitizeLlHlsPlaylistUrl(
      hls.url || (hlsUrl ? playbackUrl(hlsUrl) : ""),
    );
    try {
      hls.stopLoad();
    } catch {
      /* ignore */
    }
    try {
      if (source) hls.loadSource(`${source}${source.includes("?") ? "&" : "?"}seek=${seekNonce}`);
      // 0 = beginning of this generation — never jump to the old live edge.
      hls.startLoad(0);
      video.currentTime = 0;
    } catch {
      try {
        hls.startLoad(0);
      } catch {
        /* ignore */
      }
    }
    const resumeAt = window.setTimeout(() => {
      if (hlsRef.current !== hls) return;
      if (!playbackPausedRef.current) {
        void video.play().then(() => bufferingCbRef.current?.(false)).catch(() => {
          bufferingCbRef.current?.(false);
        });
      } else {
        bufferingCbRef.current?.(false);
      }
    }, 120);
    return () => window.clearTimeout(resumeAt);
  }, [seekAttach, seekNonce, playback, canPlay, hlsUrl]);

  useLayoutEffect(() => {
    applyPlaybackMute(videoRef.current, muted, hlsRef.current);
  }, [muted]);

  useEffect(() => {
    if (!playback) return;
    const video = videoRef.current;
    if (!video) return;
    const applyRate = () => {
      const rate = Math.max(0.5, Math.min(16, playbackRateRef.current || 1));
      if (video.playbackRate !== rate) video.playbackRate = rate;
    };
    applyRate();
    video.addEventListener("ratechange", applyRate);
    video.addEventListener("playing", applyRate);
    const keep = window.setInterval(applyRate, 400);
    if (playbackPaused) {
      try {
        video.pause();
      } catch {
        /* ignore */
      }
    } else if (canPlay) {
      void video.play().catch(() => undefined);
    }
    return () => {
      video.removeEventListener("ratechange", applyRate);
      video.removeEventListener("playing", applyRate);
      window.clearInterval(keep);
    };
  }, [playback, playbackPaused, playbackRate, canPlay]);

  const delayLabel = latency != null ? `delayed ~${latency}s` : `target ~${delaySec}s`;
  // A decoded live picture stays LIVE. FAILED is only the media-timeout state,
  // and it must not cover a pane that is still showing video.
  const pictureLive = !playback && status === "live";
  const conn = pictureLive
    ? "live"
    : liveFailed
    ? "failed"
    : playbackUnavailable || !linkUp
      ? "offline"
      : status === "live"
        ? "live"
        : "connecting";
  // Recorded: never show a harsh CONNECTING pill after the first frame — keep REC
  // and only use a quiet placeholder before media arrives.
  const connLabel = pictureLive
    ? playingBadge ?? "LIVE"
    : liveFailed
    ? "FAILED"
    : stream?.runState === "recovering"
      ? "RECOVERING"
    : playHeld
      ? "QUEUED"
    : stream?.runState === "queued"
      ? "QUEUED"
    : phaseLabel
    ? playback && phaseLabel === "PLAYING"
      ? playingBadge ?? "REC"
      : phaseLabel
    : playbackUnavailable
      ? "NONE"
      : !linkUp
        ? "OFFLINE"
        : status === "live"
          ? playingBadge ?? "LIVE"
          : playback
            ? "BUFFERING"
            : status === "error"
              ? "ERROR"
              : "STARTING";
  const placeholder = pictureLive
    ? ""
    : playbackAuth
    ? "Playback unauthorized"
    : playHeld
      ? "Click to play"
      : stream?.runState === "queued"
        ? `Queued${stream.queuePosition ? ` (${stream.queuePosition})` : ""}`
    : stream?.runState === "recovering"
      ? "Reconnecting camera…"
      : liveFailed
    ? stream?.failReason || "Start timed out."
    : codecFault
    ? "Codec error."
    : playbackUnavailable
    ? emptyLabel || "No recording for this camera"
    : !linkUp
      ? emptyLabel || "Device Offline"
      : status === "live"
        ? ""
        : status === "error" && !playback
          ? emptyLabel || "Stream Failed"
          : emptyLabel ||
            (playback
              ? "Loading recording from memory card…"
              : streamLive
                ? "Reconnecting camera…"
                : "Connecting");

  return (
    <article
      className={`${embedded ? "video-pane" : "video-tile"} ${selected && !embedded ? "selected" : ""}`}
      onClick={onSelect}
    >
      <div className="video-stage">
        <div className="video-frame">
        <video ref={videoRef} muted={muted} playsInline autoPlay />
        {status !== "live" && <div className="video-placeholder">{placeholder}</div>}
        {playHeld && onPlay ? (
          <button
            type="button"
            className="pane-retry"
            onClick={(e) => {
              e.stopPropagation();
              onPlay();
            }}
          >
            Click to play
          </button>
        ) : null}
        { (liveFailed || playbackAuth) && onRetry ? (
          <button
            type="button"
            className="pane-retry"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
          >
            Retry
          </button>
        ) : null}
        {sourceStall && status === "live" && !playback ? (
          <div className="video-stall" role="status">Live stream temporarily unavailable</div>
        ) : null}
        {embedded && (
          <div className="video-badges">
            <span className="cam-badge">{deviceLabel}</span>
            <span className={`pill ${conn}${playback && connLabel === "REC" ? " is-rec" : ""}`}>{connLabel}</span>
            {status === "live" && deviceOnline && !hideDelay && <span className="delay">{delayLabel}</span>}
          </div>
        )}
        {playback && absoluteClockLabel ? (
          <div className="video-abs-clock" aria-live="polite">
            {absoluteClockLabel}
          </div>
        ) : null}
        {playback && exportClip && !playbackUnavailable ? (
          <RecordingExportButtons spec={exportClip} ready={status === "live"} />
        ) : null}
        </div>
      </div>
      {!embedded && (
        <footer className="video-meta">
          <div>
            <strong>{deviceLabel}</strong>
            <span className={`pill ${conn}${playback && connLabel === "REC" ? " is-rec" : ""}`}>{connLabel}</span>
            {deviceName ? <span className="video-device">{deviceName}</span> : null}
          </div>
          <div className="video-actions">
            {status === "live" && !hideDelay && <span className="delay">{delayLabel}</span>}
            {controlled ? null : (
              <button
                type="button"
                className={`mute-btn audio-btn ${muted ? "is-muted" : "is-on"}`}
                title={muted ? "Unmute camera audio" : "Mute camera audio"}
                aria-label={muted ? "Unmute camera audio" : "Mute camera audio"}
                aria-pressed={!muted}
                onClick={(e) => {
                  e.stopPropagation();
                  setInternalMuted((m) => !m);
                }}
              >
                {muted ? (
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M5 10v4h3l4 4V6L8 10H5z" />
                    <path fill="currentColor" d="M15.2 9.1 16.6 7.7 20.3 11.4 22 9.7 23.4 11.1 21.7 12.8 23.4 14.5 22 15.9 20.3 14.2 16.6 17.9 15.2 16.5 18.9 12.8z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M5 10v4h3l4 4V6L8 10H5zm11.5 2a3.5 3.5 0 0 0-1.8-3.1v6.2A3.5 3.5 0 0 0 16.5 12zm0-7.3v2.1A6.5 6.5 0 0 1 20 12a6.5 6.5 0 0 1-3.5 5.2v2.1A8.5 8.5 0 0 0 22 12a8.5 8.5 0 0 0-5.5-7.3z" />
                  </svg>
                )}
                <span className="mute-btn-label">{muted ? "Unmute" : "Mute"}</span>
              </button>
            )}
          </div>
        </footer>
      )}
    </article>
  );
}
